<template lang="pug">
  .wishlist(v-if="items.length > 0 && !isCustomProduct")
    registry-drawer(v-if="addToRegistry" :buyRequest="items.length === 1 ? items[0] : items" @hide="registryHide")
    wishlist-drawer(v-if="addToWishlist && items.some(i => i.type === 'group')" :buyRequest="items[0]" :validate="validate" @hide="wishlistHide")
    rogue-link.link(v-else-if="inWishlist" to="/account/wishlist")
      Icon(name="check" fill="green")
      | {{__('Added to Wishlist')}}
    button.link(v-else @click="addItem('wishlist')" data-cnstrc-btn="add_to_wishlist")
      Spinner.spin(v-if='loading.wishlist' size="s" color="black")
      Icon.icon-wishlist(v-else name="wishlist" fill="black")
      | {{__('Add to Wishlist')}}
    button.registry.link(@click="addItem('registry')" data-cnstrc-btn="add_to_wishlist")
      Spinner.spin(v-if='loading.registry' size="s" color="black")
      Icon.icon-wishlist(v-else name="registry" fill="black")
      | {{__('Add to Registry')}}
</template>
<script lang="ts" setup>
import { defineAsyncComponent, ref, reactive, computed, watch, onMounted } from 'vue'
import type {PropType} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useListStore } from '@/js/stores/listStore'
import { useUIStore } from '@/js/stores/uiStore'
import { findDuplicateItem } from '@/js/stores/list-fns'
import { messages } from '@/js/app-bridge/messages'
import { useProductStore } from '@/js/stores/productStore'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/RogueTypes'
import { useMassStorage } from '@/js/composables/useMassStorage'

const RegistryDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "registries" */'./registry/registry-drawer.vue'))
const WishlistDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "wishlist" */'./registry/wishlist-drawer.vue'))

const props = defineProps({
  buyRequest: {
    type     : [Object, Array] as PropType<BuyRequest | BuyRequest[]>,
    required : true,
  },
  simple: {
    type    : Object,
    default : () => {},
  },
  validate: {
    type     : Function,
    required : false,
  },
})

const customerStore = useCustomerStore()
const listStore = useListStore()
const uiStore = useUIStore()
const productStore = useProductStore()
const route = useRoute()
const router = useRouter()

const items = computed((): BuyRequest[] => {
  const req = props.buyRequest

  return [req].flat().filter(Boolean) as BuyRequest[]
})

// The buyRequest prop accepts an array, but useMassStorage takes a single request, so the first item stands in for the group — the same basis `product` uses below.
const { is3DStorageProduct } = useMassStorage(computed(() => items.value[0]))

const loaded = ref(false)
const addToRegistry = ref(false)
const addToWishlist = ref(false)
const messagesRef = ref<string[]>([])

const loading = reactive({
  wishlist : false,
  registry : false,
})

const rogue = useRogue()

const loggedIn = computed(() => customerStore.loggedIn)
const wishlist = computed(() => listStore.wishlist)
const products = computed(() => productStore.products)
const product = computed(() => {
  const firstId = items.value[0]?.productId

  return firstId ? productStore.products[firstId] : undefined
})

function findRecipeId(buyRequests: BuyRequest[]): string | undefined {
  for (const buyRequest of buyRequests) {
    if (buyRequest.metadata?.recipeId) {
      return String(buyRequest.metadata.recipeId)
    }

    const childRecipeId = findRecipeId(buyRequest.children || [])

    if (childRecipeId) {
      return childRecipeId
    }
  }

  return undefined
}

function pathWithFlag(flag: string) {
  const recipeId = findRecipeId(items.value)
  const query = { ...route.query, [flag]: 1 }

  if (recipeId) {
    query.rid = recipeId
  }

  return router.resolve({ path: route.path, query, hash: route.hash }).fullPath
}

function loginPathWithFlag(flag: string) {
  return `/login?redirect=${encodeURIComponent(pathWithFlag(flag))}`
}

function removeFlag(flag: string) {
  const query = { ...route.query }

  delete query[flag]
  router.replace({ path: route.path, query, hash: route.hash })
}

const isTheCave = computed(() => isEnabledCustomProduct(rogue, 'cave', product.value))
const isSlingerX = computed(() => isEnabledCustomProduct(rogue, 'slinger-x', product.value))
const isHybridRig = computed(() => isEnabledCustomProduct(rogue, 'hybrid-rig', product.value))
const isCustomProduct = computed(() => is3DStorageProduct.value || isTheCave.value || isSlingerX.value || isHybridRig.value)

const inWishlist = computed(() => {
  if (loaded.value) {
    try {
      return items.value.every(item => findDuplicateItem(item, wishlist.value.items))
    } catch (e) {
      // this is an empty catch
    }
  }

  return false
})

watch(
  () => 'props.buyRequest.productId',
  () => {
    messagesRef.value = []
  }
)

onMounted(() => {
  loaded.value = true

  if (route.query.openWishlist) {

    removeFlag('openWishlist')
    const isGroup = items.value.some(item => item.type === 'group')

    if(isGroup){
      setTimeout(() => {
        const target =
      document.querySelector('.pp-rhpa') ||
      document.querySelector('.grid-rhpa')

        if (target) {
          target.scrollIntoView()
        }
        uiStore.showOverlay('wishlist', rogue)
        addToWishlist.value = true
      }, 100)
    }
    else{
      setTimeout(() => {
        document.getElementsByClassName('grid-rhpa')[0]?.scrollIntoView()
        finishWishlistAdd()
      }, 100)
    }

  }
  if (route.query.openRegistry) {
    addToRegistry.value = true
    removeFlag('openRegistry')
    setTimeout(() => {
      document.getElementsByClassName('grid-rhpa')[0]?.scrollIntoView()
    }, 100)
  }
})

async function addItem(requestType: 'wishlist' | 'registry') {
  loading[requestType] = true

  const hasGroup = items.value.some(i => i.type === 'group')

  const drawerWillValidate = requestType === 'wishlist' && hasGroup && loggedIn.value

  try {
    if (!drawerWillValidate && props.validate && !(await props.validate())) {
      loading[requestType] = false

      return
    }
    if (requestType === 'wishlist') {
      if (hasGroup && loggedIn.value) {
        addToWishlist.value = true
      } else if (!loggedIn.value && !rogue.appContext) {
        if (route.meta?.type === 'cms') {
          router.push(`/login?redirect=${encodeURIComponent(route.fullPath)}`)
        } else {
          router.push(loginPathWithFlag('openWishlist'))
        }
      } else if (hasGroup && rogue.isApp('3.0.5')) {
        if (route.meta?.type === 'cms') {
          rogue.emitToApp(messages.LOGIN, { redirect: route.fullPath })
        } else {
          rogue.emitToApp(messages.LOGIN, { redirect: pathWithFlag('openWishlist') })
        }
      } else {
        wishlistAdd()
      }
    }
    if (requestType === 'registry') {
      if (loggedIn.value) {
        addToRegistry.value = true
        uiStore.showOverlay('registry', rogue)
      } else if (rogue.appContext) {
        rogue.emitToApp(messages.LOGIN, { redirect: pathWithFlag('openRegistry') })
      } else {
        router.push(loginPathWithFlag('openRegistry'))
      }
    }
  } catch (e) {
    rogue.log.warn('ERROR: Could not add items to wishlist or registry')
  }
  loading[requestType] = false
}

async function finishWishlistAdd(attemptsLeft = 10) {
  try {
    if (props.validate && !(await props.validate())) {
      return
    }
  } catch {
    if (attemptsLeft > 0) {
      setTimeout(() => finishWishlistAdd(attemptsLeft - 1), 250)

      return
    }

    rogue.log.warn('ERROR: Could not rebuild the customization when adding to the wishlist after login')
  }

  wishlistAdd()
}

function wishlistAdd() {
  const uniqueItems = items.value
    .filter(i => !findDuplicateItem(i, wishlist.value.items))
    .map(i => Object.assign({}, i, { qty: Math.max(1, i.qty) }))

  listStore.addListItems(rogue, 'wishlist', uniqueItems)
  const hasGroup = items.value.some(i => i.type === 'group')

  if (uniqueItems.length > 1 || hasGroup) {
    messagesRef.value = uniqueItems.map(item => String(item.productId))
    
    setTimeout(() => {
      messagesRef.value = []
    }, 200)
  }
}

function wishlistHide() {
  addToWishlist.value = false
  loading.wishlist = false
  uiStore.hideOverlay()
}
    
function registryHide() {
  addToRegistry.value = false
  loading.registry = false
  uiStore.hideOverlay()
}
</script>

<style scoped lang="scss">

.registry {
  margin-left: $space-s;
}

.wishlist {
  margin-top: $space-m;
  display: flex;

  .link, .wishlist-message {
    color: $color-black;
    font-size: $size-xs;
    display: flex;
    align-items: center;
    margin-bottom: $space-xs;

    .icon {
      width: 15px;
      height: 15px;
      margin-right: $space-xxs;
    }
  }
}

</style>
